import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import LoadingSpinner from '../../components/common/LoadingSpinner'
import axios from 'axios'
import { toast } from 'react-toastify'
import { format } from 'date-fns'
import { FaTrash, FaExclamationTriangle } from 'react-icons/fa'

const GymList = () => {
  const { user, hasPermission } = useAuth()
  const [allGyms, setAllGyms] = useState([])
  const [loading, setLoading] = useState(true)
  const [filters, setFilters] = useState({
    search: '',
    stage: '',
    city: '',
    showMyGyms: user?.role?.level === 4 // BD Executive sees only their gyms by default
  })
  const [editingNotes, setEditingNotes] = useState({})
  const [deleteConfirm, setDeleteConfirm] = useState({ show: false, gym: null })
  const [isDeleting, setIsDeleting] = useState(false)

  const isBDExecutive = user?.role?.level === 4
  const isCOO = user?.role?.level === 2
  const isCEO = user?.role?.level === 1

  // Initial load only
  useEffect(() => {
    fetchGyms()
  }, [])

  const fetchGyms = async () => {
    try {
      setLoading(true)
      const params = new URLSearchParams()

      // Fetch all gyms without pagination limit
      params.append('limit', '1000')

      const response = await axios.get(`/api/gyms?${params.toString()}`)
      setAllGyms(response.data.gyms)
    } catch (error) {
      console.error('Error fetching gyms:', error)
      toast.error('Failed to load gyms')
    } finally {
      setLoading(false)
    }
  }

  // Client-side filtering
  const filteredGyms = allGyms.filter(gym => {
    const matchesSearch = !filters.search ||
      gym.name.toLowerCase().includes(filters.search.toLowerCase()) ||
      gym.address?.toLowerCase().includes(filters.search.toLowerCase())
    const matchesStage = !filters.stage || gym.stage === filters.stage
    const matchesCity = !filters.city ||
      gym.location?.toLowerCase().includes(filters.city.toLowerCase())
    // My Gyms Only filter - works for all roles (CEO, COO, HOA, BD)
    const matchesMyGyms = !filters.showMyGyms || gym.assignedUserId === user.id || gym.createdBy === user.id
    return matchesSearch && matchesStage && matchesCity && matchesMyGyms
  })

  const handleNotesUpdate = async (gymId, notes) => {
    try {
      await axios.put(`/api/gyms/${gymId}`, { internalNotes: notes })
      setAllGyms(prev => prev.map(gym =>
        gym.id === gymId ? { ...gym, internalNotes: notes } : gym
      ))
      setEditingNotes(prev => ({ ...prev, [gymId]: false }))
      toast.success('Notes updated successfully')
    } catch (error) {
      console.error('Error updating notes:', error)
      toast.error('Failed to update notes')
    }
  }

  const handleDeleteGym = async () => {
    if (!deleteConfirm.gym) return

    setIsDeleting(true)
    try {
      await axios.delete(`/api/gyms/${deleteConfirm.gym.id}`)

      // Remove the gym from the local state
      setAllGyms(prev => prev.filter(gym => gym.id !== deleteConfirm.gym.id))
      toast.success(`Gym "${deleteConfirm.gym.name}" deleted successfully`)
      setDeleteConfirm({ show: false, gym: null })
    } catch (error) {
      console.error('Error deleting gym:', error)
      const message = error.response?.data?.error || 'Failed to delete gym'
      toast.error(message)
    } finally {
      setIsDeleting(false)
    }
  }

  const getStageColor = (stage) => {
    const colors = {
      'New Lead': 'bg-gray-100 text-gray-800',
      'Site Visit': 'bg-blue-100 text-blue-800',
      'Proposal Sent': 'bg-yellow-100 text-yellow-800',
      'Agreement Signed': 'bg-green-100 text-green-800',
      'Facility Work': 'bg-purple-100 text-purple-800',
      'Post Reki Approved': 'bg-indigo-100 text-indigo-800',
      'Gym Onboard': 'bg-red-100 text-red-800'
    }
    return colors[stage] || 'bg-gray-100 text-gray-800'
  }

  const getStageProgress = (stage) => {
    const stages = ['New Lead', 'Site Visit', 'Proposal Sent', 'Agreement Signed', 'Facility Work', 'Post Reki Approved', 'Gym Onboard']
    const index = stages.indexOf(stage)
    return index >= 0 ? ((index + 1) / stages.length) * 100 : 0
  }

  const canUserModifyGym = (gym) => {
    if (isBDExecutive) {
      return gym.assignedUserId === user.id
    }
    return hasPermission(3) // Head of Acquisition and above
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {isBDExecutive ? 'My Gyms' : 'All Gyms'}
          </h1>
          <p className="mt-1 text-sm text-gray-600">
            {isBDExecutive 
              ? 'Manage gyms you\'ve added to the acquisition pipeline'
              : 'Manage gym acquisition pipeline'
            }
          </p>
        </div>
        
        <div className="flex items-center space-x-4">
          {hasPermission(4) && (
            <Link
              to="/gyms/create"
              className="btn-primary"
            >
              Add New Gym
            </Link>
          )}
          
          <Link
            to="/kanban"
            className="btn-secondary"
          >
            Kanban View
          </Link>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="card-body py-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Search Gyms
              </label>
              <input
                type="text"
                value={filters.search}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                className="input"
                placeholder="Search by name, address..."
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Stage
              </label>
              <select
                value={filters.stage}
                onChange={(e) => setFilters(prev => ({ ...prev, stage: e.target.value }))}
                className="input"
              >
                <option value="">All Stages</option>
                <option value="New Lead">New Lead</option>
                <option value="Site Visit">Site Visit</option>
                <option value="Proposal Sent">Proposal Sent</option>
                <option value="Agreement Signed">Agreement Signed</option>
                {!isBDExecutive && (
                  <>
                    <option value="Facility Work">Facility Work</option>
                    <option value="Post Reki Approved">Post Reki Approved</option>
                    {isCEO && <option value="Gym Onboard">Gym Onboard</option>}
                  </>
                )}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                City
              </label>
              <input
                type="text"
                value={filters.city}
                onChange={(e) => setFilters(prev => ({ ...prev, city: e.target.value }))}
                className="input"
                placeholder="Filter by city"
              />
            </div>
            
            <div className="flex items-end">
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={filters.showMyGyms}
                  onChange={(e) => setFilters(prev => ({ ...prev, showMyGyms: e.target.checked }))}
                  className="rounded border-gray-300 text-wtf-red focus:ring-red-500"
                />
                <span className="ml-2 text-sm text-gray-700">My Gyms Only</span>
              </label>
            </div>
          </div>
        </div>
      </div>

      {/* Gym List */}
      {filteredGyms.length === 0 ? (
        <div className="card">
          <div className="card-body text-center py-12">
            <div className="text-gray-400 text-6xl mb-4">🏋️</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No gyms found</h3>
            <p className="text-gray-600 mb-4">
              {isBDExecutive
                ? "You haven't added any gyms yet. Create your first gym to get started."
                : "No gyms match your current filters."
              }
            </p>
            {isBDExecutive && (
              <Link to="/gyms/create" className="btn-primary">
                Add Your First Gym
              </Link>
            )}
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredGyms.map(gym => (
            <div key={gym.id} className="card hover:shadow-md transition-shadow">
              <div className="card-body">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <Link
                        to={`/gyms/${gym.id}`}
                        className="text-lg font-semibold text-gray-900 hover:text-wtf-red"
                      >
                        {gym.name}
                      </Link>
                      <span className={`badge ${getStageColor(gym.stage)}`}>
                        {gym.stage}
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-600">
                      <div>
                        <span className="font-medium">Address:</span>
                        <div className="mt-1">{gym.address}</div>
                      </div>
                      
                      <div>
                        <span className="font-medium">City:</span> {gym.location}
                        {gym.assignedManager && (
                          <div className="mt-1">
                            <span className="font-medium">Manager:</span> {gym.assignedManager.name}
                          </div>
                        )}
                      </div>
                      
                      <div>
                        {gym.targetLaunchDate && (
                          <div>
                            <span className="font-medium">Target Launch:</span>
                            <div className={`mt-1 ${
                              new Date(gym.targetLaunchDate) < new Date() 
                                ? 'text-red-600' 
                                : 'text-green-600'
                            }`}>
                              {format(new Date(gym.targetLaunchDate), 'MMM dd, yyyy')}
                            </div>
                          </div>
                        )}
                        
                        <div className="mt-2">
                          <span className="font-medium">Created:</span> {format(new Date(gym.createdAt), 'MMM dd, yyyy')}
                        </div>
                      </div>
                    </div>

                    {/* Progress Bar */}
                    <div className="mt-4">
                      <div className="flex justify-between text-sm text-gray-600 mb-1">
                        <span>Progress</span>
                        <span>{Math.round(getStageProgress(gym.stage))}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-wtf-red h-2 rounded-full transition-all duration-300"
                          style={{ width: `${getStageProgress(gym.stage)}%` }}
                        ></div>
                      </div>
                    </div>

                    {/* Internal Notes for BD Executive */}
                    {canUserModifyGym(gym) && (
                      <div className="mt-4 p-3 bg-yellow-50 rounded-lg">
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-sm font-medium text-gray-700">Internal Notes</span>
                          {!editingNotes[gym.id] && (
                            <button
                              onClick={() => setEditingNotes(prev => ({ ...prev, [gym.id]: true }))}
                              className="text-xs text-wtf-red hover:text-red-700"
                            >
                              Edit
                            </button>
                          )}
                        </div>
                        
                        {editingNotes[gym.id] ? (
                          <div className="space-y-2">
                            <textarea
                              defaultValue={gym.internalNotes || ''}
                              className="w-full p-2 border border-gray-300 rounded text-sm"
                              rows={3}
                              placeholder="Add internal notes for tracking..."
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' && e.ctrlKey) {
                                  handleNotesUpdate(gym.id, e.target.value)
                                }
                              }}
                              id={`notes-${gym.id}`}
                            />
                            <div className="flex space-x-2">
                              <button
                                onClick={() => {
                                  const notes = document.getElementById(`notes-${gym.id}`).value
                                  handleNotesUpdate(gym.id, notes)
                                }}
                                className="btn-primary text-xs py-1 px-2"
                              >
                                Save
                              </button>
                              <button
                                onClick={() => setEditingNotes(prev => ({ ...prev, [gym.id]: false }))}
                                className="btn-secondary text-xs py-1 px-2"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <p className="text-sm text-gray-600">
                            {gym.internalNotes || 'No notes added yet. Click Edit to add notes.'}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                  
                  <div className="flex flex-col space-y-2 ml-4">
                    <Link
                      to={`/gyms/${gym.id}`}
                      className="btn-secondary text-sm"
                    >
                      View Details
                    </Link>
                    
                    {canUserModifyGym(gym) && (
                      <Link
                        to={`/gyms/${gym.id}/edit`}
                        className="btn-primary text-sm"
                      >
                        Edit
                      </Link>
                    )}
                    
                    {canUserModifyGym(gym) && (
                      <button
                        onClick={() => setDeleteConfirm({ show: true, gym })}
                        className="btn-danger text-sm"
                        title="Delete gym"
                      >
                        Delete
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Summary Stats */}
      <div className="card">
        <div className="card-body">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            {isBDExecutive ? 'My Pipeline Summary' : 'Pipeline Summary'}
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-wtf-red">{filteredGyms.length}</div>
              <div className="text-sm text-gray-600">Total Gyms</div>
            </div>

            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">
                {filteredGyms.filter(g => ['New Lead', 'Site Visit'].includes(g.stage)).length}
              </div>
              <div className="text-sm text-gray-600">Early Stage</div>
            </div>

            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">
                {filteredGyms.filter(g => ['Proposal Sent', 'Agreement Signed'].includes(g.stage)).length}
              </div>
              <div className="text-sm text-gray-600">In Progress</div>
            </div>

            <div className="text-center">
              <div className="text-2xl font-bold text-purple-600">
                {filteredGyms.filter(g => g.targetLaunchDate && new Date(g.targetLaunchDate) < new Date()).length}
              </div>
              <div className="text-sm text-gray-600">Overdue</div>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirm.show && deleteConfirm.gym && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                <FaExclamationTriangle className="text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Delete Gym</h3>
            </div>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete <span className="font-semibold">"{deleteConfirm.gym.name}"</span>? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteConfirm({ show: false, gym: null })}
                disabled={isDeleting}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteGym}
                disabled={isDeleting}
                className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {isDeleting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Deleting...
                  </>
                ) : (
                  <>
                    <FaTrash />
                    Yes, Delete
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default GymList 
